'use client';

import { useTranslation } from '@/layers/presentation/i18n/client';
import logger from '@/layers/support/monitoring/logger';
import { Button, Checkbox, Input } from '@nextui-org/react';
import { useEffect, useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / ProviderProfileCompletion]';

interface ProviderProfileCompletionProps {
  onComplete: (data: { npi: string; acknowledgement: boolean }) => Promise<void>;
  onCancel?: () => void;
  initialValues?: {
    npi: string;
    acknowledgement?: boolean;
  };
  isLoading?: boolean;
}

export function ProviderProfileCompletion({
  onComplete,
  onCancel,
  initialValues,
  isLoading = false,
}: ProviderProfileCompletionProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    npi: initialValues?.npi || '',
    acknowledgement: initialValues?.acknowledgement || false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when initialValues change
  useEffect(() => {
    if (initialValues) {
      setFormData({
        npi: initialValues.npi || '',
        acknowledgement: initialValues?.acknowledgement || false,
      });
    }
  }, [initialValues]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.npi) {
      newErrors.npi = t('common:validation.required');
    } else if (!/^\d{10}$/.test(formData.npi)) {
      newErrors.npi = t('common:validation.invalidNPI');
    }

    if (!initialValues?.npi && !formData.acknowledgement) {
      newErrors.acknowledgement = t('common:validation.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onComplete(formData);
      logger.debug(`${LOG_MODULE} Profile completion successful`, { formData });
    } catch (error) {
      logger.error(`${LOG_MODULE} Error completing profile`, { error });
      setErrors({
        form: t('common:errors.generalError'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (isSelected: boolean) => {
    setFormData(prev => ({
      ...prev,
      acknowledgement: isSelected,
    }));

    // Clear error for checkbox when user changes it
    if (errors.acknowledgement) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.acknowledgement;
        return newErrors;
      });
    }
  };

  const isShowCheckbox = !(
    initialValues?.npi &&
    initialValues?.npi.length === 10 &&
    initialValues?.acknowledgement
  );

  // Log for debugging
  useEffect(() => {
    logger.debug(`${LOG_MODULE} Rendering ProviderProfileCompletion`, {
      isShowCheckbox,
      initialValues,
      formData,
    });
  }, [isShowCheckbox, initialValues, formData]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {errors.form && <div className="text-danger text-sm mb-2">{errors.form}</div>}
      <Input
        label={t('users:providerProfile.npi')}
        name="npi"
        value={formData.npi}
        onChange={handleChange}
        isInvalid={!!errors.npi}
        errorMessage={errors.npi}
        description={t('users:providerProfile.npiDescription')}
      />
      {isShowCheckbox && (
        <Checkbox
          isSelected={formData.acknowledgement}
          onValueChange={handleCheckboxChange}
          isInvalid={!!errors.acknowledgement}
          className="flex items-start gap-2 mb-2"
        >
          I confirm that I am a licensed healthcare provider or an authorized representative acting
          on behalf of one.
          {errors.acknowledgement && (
            <div className="text-danger text-xs mt-2">{errors.acknowledgement}</div>
          )}
        </Checkbox>
      )}
      <div className="flex w-full">
        {onCancel && (
          <Button variant="flat" onPress={onCancel} disabled={isSubmitting || isLoading}>
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button
          size="lg"
          color="primary"
          type="submit"
          className="w-full"
          isLoading={isSubmitting || isLoading}
        >
          {t('users:providerProfile.updateProfile')}
        </Button>
      </div>
    </form>
  );
}
